import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileDown, BookOpen } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/pages/Home";
import api from "@/lib/api";

const TABS = ["articles", "resources"];

export default function Insights() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  const [tab, setTab] = useState("articles");
  const [posts, setPosts] = useState([]);
  const [resources, setResources] = useState([]);

  useSEO({
    lang,
    title: `${t("insightsPage.label")} | HO-SYS Advanced Technologies`,
    description: t("insightsPage.subtitle"),
    path: "/insights",
  });

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        <Reveal>
          <SectionLabel>{t("insightsPage.label")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("insightsPage.title")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">
            {t("insightsPage.subtitle")}
          </p>
        </Reveal>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div
          role="tablist"
          data-testid="insights-tabs"
          className="inline-flex rounded-full border border-white/10 bg-[#0A0A0A] p-1"
        >
          {TABS.map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              data-testid={`insights-tab-${k}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === k
                  ? "bg-neon text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {t(`insightsPage.tabs.${k}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Articles */}
      {tab === "articles" && (
        <section
          data-testid="insights-articles"
          className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24"
        >
          {posts.length === 0 ? (
            <p className="text-white/40">{t("blog.empty")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.06}>
                  <Link to={L(`/blog/${p.id}`)} data-testid={`insights-article-${p.id}`}>
                    <motion.div
                      whileHover={{ y: -5 }}
                      className="group h-full rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A] hover:border-neon/40 transition-colors"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={p.cover}
                          alt={ml(p.title)}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                        />
                        <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neon border border-neon/30">
                          {p.tag}
                        </span>
                      </div>
                      <div className="p-6">
                        <h3 className="font-display text-xl font-semibold text-white leading-snug">
                          {ml(p.title)}
                        </h3>
                        <p className="mt-3 text-white/50 text-sm leading-relaxed line-clamp-3">
                          {ml(p.excerpt)}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-neon">
                          {t("common.readMore")}
                          <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                    </motion.div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Resources */}
      {tab === "resources" && (
        <section
          data-testid="insights-resources"
          className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24"
        >
          {resources.length === 0 ? (
            <p className="text-white/40">{t("blog.empty")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((r, i) => {
                const filePath = r.file?.replace("{lang}", lang) || "";
                return (
                  <Reveal key={r.id || r.slug || i} delay={(i % 3) * 0.06}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      data-testid={`insights-resource-${r.slug || i}`}
                      className="h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors flex flex-col"
                    >
                      <div className="w-11 h-11 rounded-xl bg-neon/10 border border-neon/25 flex items-center justify-center mb-4">
                        <BookOpen className="w-5 h-5 text-neon" />
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {t(`resourcesPage.categories.${r.category}`, r.category)}
                      </span>
                      <h3 className="mt-1 font-display text-lg font-semibold text-white">
                        {ml(r.title)}
                      </h3>
                      <p className="mt-2 text-white/55 text-sm leading-relaxed line-clamp-3 grow">
                        {ml(r.description)}
                      </p>
                      {filePath && (
                        <a
                          href={filePath}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`insights-resource-download-${r.slug || i}`}
                          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neon"
                        >
                          <FileDown className="w-4 h-4" />
                          {t("resourcesPage.download")}
                        </a>
                      )}
                    </motion.div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
